// Pure draw function for Shortbread vector tiles (VersaTiles), usable both from the render
// worker and from the main-thread fallback. No DOM/Worker globals here — only Canvas2D.
//
// Style: light, calm, no labels; must not compete with the red/orange zone overlays or the blue
// slipway markers drawn on top by mapView. Order: land -> water -> buildings -> streets (casing
// then fill) -> piers. Roughly VersaTiles "Colorful", muted. Unknown `kind` values are ignored.

export interface VTPoint {
  x: number;
  y: number;
}

export interface VTFeature {
  /** VectorTileFeature.type: 1 = Point, 2 = LineString, 3 = Polygon. */
  type: number;
  properties: Record<string, number | string | boolean>;
  /** Rings/lines in tile coordinate space [0, extent]. */
  geometry: VTPoint[][];
}

export type VTLayers = Record<string, VTFeature[]>;

/** Sub-square of tile coordinate space to render, in the same units as `extent` (for overzoom). */
export interface DrawCrop {
  x0: number;
  y0: number;
  size: number;
}

export interface DrawOpts {
  /** Output canvas size in px (square). */
  size: number;
  /** Tile coordinate extent (e.g. 4096). */
  extent: number;
  /** Effective (virtual) zoom, used for line widths — not scaled by overzoom cropping. */
  zoom: number;
  crop?: DrawCrop;
}

/** Common subset of CanvasRenderingContext2D / OffscreenCanvasRenderingContext2D used here. */
export type Ctx2D = Pick<
  CanvasRenderingContext2D,
  'save' | 'restore' | 'clearRect' | 'fillRect' | 'fill' | 'stroke' | 'setLineDash'
> & {
  fillStyle: string | CanvasGradient | CanvasPattern;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  lineWidth: number;
  lineCap: CanvasLineCap;
  lineJoin: CanvasLineJoin;
};

const LAND = '#f3f1ec';
const WATER = '#c6dff0';
const FOREST = '#dcebd0';
const GRASS = '#e6f0da';
const BUILT = '#ece8e0';
const BUILDING_FILL = '#e2ddd6';
const BUILDING_STROKE = '#c9c1b4';
const STREET_CASING = '#d9d5cb';
const STREET_FILL = '#ffffff';
const RAIL = '#b5b2ac';
const PIER = '#e6e1d8';

const LAND_KIND_COLOR: Record<string, string> = {
  forest: FOREST,
  wood: FOREST,
  grass: GRASS,
  park: GRASS,
  meadow: GRASS,
  village_green: GRASS,
  cemetery: GRASS,
  recreation_ground: GRASS,
  farmland: GRASS,
  residential: BUILT,
  commercial: BUILT,
  industrial: BUILT,
  retail: BUILT,
};

// Base line widths in output px at zoom 15; scaled by zoom via widthForZoom().
const STREET_WIDTH: Record<string, number> = {
  motorway: 5,
  trunk: 4.4,
  primary: 3.8,
  secondary: 3.2,
  tertiary: 2.6,
  residential: 2,
  unclassified: 2,
  living_street: 1.8,
  pedestrian: 1.6,
  service: 1.3,
  track: 1,
  path: 0.9,
  footway: 0.9,
  cycleway: 0.9,
  steps: 0.9,
};
const DEFAULT_STREET_WIDTH = STREET_WIDTH.service;
const DASHED_KINDS = new Set(['track', 'path', 'footway', 'cycleway', 'steps']);

/** Zoom-scaled line width in output px (not affected by overzoom cropping). */
function widthForZoom(base: number, zoom: number): number {
  const f = Math.pow(1.28, zoom - 15);
  return Math.max(base * 0.45, Math.min(base * 3, base * f));
}

function ringPath(path: Path2D, pts: VTPoint[], ox: number, oy: number, scale: number): void {
  if (pts.length === 0) return;
  path.moveTo((pts[0].x - ox) * scale, (pts[0].y - oy) * scale);
  for (let i = 1; i < pts.length; i++) path.lineTo((pts[i].x - ox) * scale, (pts[i].y - oy) * scale);
  path.closePath();
}

function linePath(path: Path2D, pts: VTPoint[], ox: number, oy: number, scale: number): void {
  if (pts.length === 0) return;
  path.moveTo((pts[0].x - ox) * scale, (pts[0].y - oy) * scale);
  for (let i = 1; i < pts.length; i++) path.lineTo((pts[i].x - ox) * scale, (pts[i].y - oy) * scale);
}

/** Builds a combined polygon path for all polygon features, or null if there are none to draw. */
function polygonPath(features: VTFeature[] | undefined, ox: number, oy: number, scale: number): Path2D | null {
  if (!features || features.length === 0) return null;
  const p = new Path2D();
  let any = false;
  for (const f of features) {
    if (f.type !== 3) continue;
    for (const r of f.geometry) {
      ringPath(p, r, ox, oy, scale);
      any = true;
    }
  }
  return any ? p : null;
}

export function drawShortbreadTile(ctx: Ctx2D, layers: VTLayers, opts: DrawOpts): void {
  const { size, extent, zoom } = opts;
  const crop = opts.crop ?? { x0: 0, y0: 0, size: extent };
  const scale = crop.size > 0 ? size / crop.size : 1;
  const ox = crop.x0;
  const oy = crop.y0;

  ctx.save();
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = LAND;
  ctx.fillRect(0, 0, size, size);

  // Land use (forest, grass, residential, ...); unmatched kinds keep the land background.
  const land = layers.land;
  if (land) {
    for (const f of land) {
      if (f.type !== 3) continue;
      const kind = String(f.properties.kind ?? '');
      const color = LAND_KIND_COLOR[kind];
      if (!color) continue;
      const p = new Path2D();
      for (const r of f.geometry) ringPath(p, r, ox, oy, scale);
      ctx.fillStyle = color;
      ctx.fill(p, 'evenodd');
    }
  }

  // Water.
  const oceanPath = polygonPath(layers.ocean, ox, oy, scale);
  const waterPath = polygonPath(layers.water_polygons, ox, oy, scale);
  if (oceanPath || waterPath) {
    ctx.fillStyle = WATER;
    if (oceanPath) ctx.fill(oceanPath, 'evenodd');
    if (waterPath) ctx.fill(waterPath, 'evenodd');
  }
  const waterLines = layers.water_lines;
  if (waterLines) {
    ctx.strokeStyle = WATER;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash([]);
    for (const f of waterLines) {
      if (f.type !== 2) continue;
      const kind = String(f.properties.kind ?? '');
      ctx.lineWidth = widthForZoom(kind === 'river' ? 2 : 1.2, zoom);
      const p = new Path2D();
      for (const r of f.geometry) linePath(p, r, ox, oy, scale);
      ctx.stroke(p);
    }
  }

  // Buildings.
  const buildingsPath = polygonPath(layers.buildings, ox, oy, scale);
  if (buildingsPath) {
    ctx.fillStyle = BUILDING_FILL;
    ctx.fill(buildingsPath, 'evenodd');
    if (zoom >= 15) {
      ctx.strokeStyle = BUILDING_STROKE;
      ctx.lineWidth = 0.8;
      ctx.setLineDash([]);
      ctx.stroke(buildingsPath);
    }
  }

  // Streets: casing pass, then fill pass (roads render on top of each other in kind order).
  const streets = layers.streets;
  if (streets) {
    const roads = streets.filter((f) => f.type === 2 && String(f.properties.kind ?? '') !== 'rail');
    const rails = streets.filter((f) => f.type === 2 && String(f.properties.kind ?? '') === 'rail');
    roads.sort(
      (a, b) =>
        (STREET_WIDTH[String(a.properties.kind ?? '')] ?? DEFAULT_STREET_WIDTH) -
        (STREET_WIDTH[String(b.properties.kind ?? '')] ?? DEFAULT_STREET_WIDTH),
    );
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash([]);
    for (const f of roads) {
      const kind = String(f.properties.kind ?? '');
      const base = STREET_WIDTH[kind] ?? DEFAULT_STREET_WIDTH;
      const w = widthForZoom(base, zoom);
      ctx.strokeStyle = STREET_CASING;
      ctx.lineWidth = w + 1.2;
      const p = new Path2D();
      for (const r of f.geometry) linePath(p, r, ox, oy, scale);
      ctx.stroke(p);
    }
    for (const f of roads) {
      const kind = String(f.properties.kind ?? '');
      const base = STREET_WIDTH[kind] ?? DEFAULT_STREET_WIDTH;
      const w = widthForZoom(base, zoom);
      ctx.strokeStyle = STREET_FILL;
      ctx.lineWidth = w;
      ctx.setLineDash(DASHED_KINDS.has(kind) ? [w * 1.5, w * 1.5] : []);
      const p = new Path2D();
      for (const r of f.geometry) linePath(p, r, ox, oy, scale);
      ctx.stroke(p);
    }
    ctx.setLineDash([]);
    if (rails.length > 0) {
      ctx.strokeStyle = RAIL;
      ctx.lineWidth = widthForZoom(1.2, zoom);
      for (const f of rails) {
        const p = new Path2D();
        for (const r of f.geometry) linePath(p, r, ox, oy, scale);
        ctx.stroke(p);
      }
    }
  }

  // Piers, in the land/pier colour (drawn last so they read as a walkway over the water).
  const pierPolys = polygonPath(layers.pier_polygons, ox, oy, scale);
  if (pierPolys) {
    ctx.fillStyle = PIER;
    ctx.fill(pierPolys, 'evenodd');
  }
  const pierLines = layers.pier_lines;
  if (pierLines && pierLines.length > 0) {
    ctx.strokeStyle = PIER;
    ctx.lineWidth = widthForZoom(1.6, zoom);
    ctx.lineCap = 'round';
    ctx.setLineDash([]);
    const p = new Path2D();
    for (const f of pierLines) {
      if (f.type !== 2) continue;
      for (const r of f.geometry) linePath(p, r, ox, oy, scale);
    }
    ctx.stroke(p);
  }

  ctx.restore();
}
